import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Modal from "antd/lib/modal/Modal";
import Card from "antd/lib/card";
import Tag from "antd/lib/tag";
import Table from "antd/lib/table";
import Input from "antd/lib/input";
import dayjs from "dayjs";
import 'dayjs/locale/en';

import { IconCloudStorm, IconRobot, IconAlertTriangle, IconWind, IconCalendar, IconSnowflake, IconDroplet } from "@tabler/icons-react";

import styles from "./region-details-modal.module.css";

const { TextArea } = Input;

const WIND_DIRECTIONS = {
    0: 'N', 45: 'NE', 90: 'E', 135: 'SE',
    180: 'S', 225: 'SW', 270: 'W', 315: 'NW', 360: 'N'
};

const getWindDirection = (degrees) => {
    if (!degrees && degrees !== 0) return 'N/A';
    const directions = Object.entries(WIND_DIRECTIONS);
    return directions.reduce((closest, [deg, dir]) => {
        return Math.abs(deg - degrees) < Math.abs(closest[0] - degrees) ? [deg, dir] : closest;
    })[1];
};

export default function RegionDetailsModal({ selectedRegionForEdit, setSelectedRegionForEdit, regionData, setRegionData, handleSiteRemove, handleSiteSelect, generateMessageForRegion, handleMessageChange }) {
    const renderRegionModal = (region) => {
        const data = regionData[region] || {};
        return (
          <div className={styles.regionModalContent}>
            <div className={styles.regionData}>
              {/* Left Column */}
              <div className={styles.modalLeftColumn}>
                {/* Sites Selection Card */}
                <Card className={styles.modalCard}>
                  <div className={styles.cardHeader}>
                    <Typography variant="h6">Selected Sites</Typography>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Button
                        size="small"
                        onClick={() => {
                          const unselectedSites = (data.sites || []).filter(
                            site => !data.selectedSites?.some(s => s.site_id === site.site_id)
                          );
                          if (unselectedSites.length > 0) {
                            setRegionData(prev => ({
                              ...prev,
                              [region]: {
                                ...prev[region],
                                selectedSites: [...(prev[region].selectedSites || []), ...unselectedSites]
                              }
                            }));
                          }
                        }}
                        className={styles.actionButton}
                        disabled={!data.sites?.length || data.sites?.length === data.selectedSites?.length}
                      >
                        Select All
                      </Button>
                      <Typography variant="body2" color="textSecondary">
                        {data.selectedSites?.length || 0} sites selected
                      </Typography>
                    </div>
                  </div>
                  <div className={styles.selectedSites}>
                    {data.selectedSites?.map((site) => (
                      <Tag
                        key={site.site_id}
                        closable
                        onClose={() => handleSiteRemove(region, site.site_id)}
                        className={styles.siteTag}
                      >
                        {site.site_name}
                      </Tag>
                    ))}
                  </div>
                  <Table
                    columns={[
                      {
                        title: 'Site Name',
                        dataIndex: 'site_name',
                        key: 'site_name'
                      },
                      {
                        title: 'Action',
                        key: 'action',
                        width: 80,
                        render: (_, record) => (
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSiteSelect(region, record);
                            }}
                            disabled={data.selectedSites?.some(
                              (site) => site.site_id === record.site_id
                            )}
                            className={styles.actionButton}
                          >
                            Select
                          </Button>
                        )
                      }
                    ]}
                    dataSource={data.sites || []}
                    rowKey="site_id"
                    size="small"
                    pagination={{ pageSize: 4 }}
                    className={styles.sitesTable}
                  />
                </Card>
    
                {/* Strategy Message Card */}
                <Card className={styles.modalCard}>
                  <div className={styles.cardHeader}>
                    <Typography variant="h6">Strategy Message</Typography>
                    <Button
                      size="small"
                      onClick={() => generateMessageForRegion(region)}
                      startIcon={<IconRobot size={16} />}
                      className={styles.generateButton}
                      disabled={data.generating}
                    >
                      {data.generating ? 'Generating...' : 'Generate'}
                    </Button>
                  </div>
                  <TextArea
                    value={data.message || ''}
                    onChange={(e) => handleMessageChange(region, e.target.value)}
                    rows={4}
                    className={styles.messageInput}
                    placeholder="Enter your storm strategy message or generate one with AI..."
                  />
                </Card>
              </div>
    
              {/* Right Column */}
              <div className={styles.modalRightColumn}>
                {/* Weather Card */}
                <Card className={styles.modalCard}>
                  <div className={styles.cardHeader}>
                    <Typography variant="h6">Weather Conditions</Typography>
                  </div>
                  {data.loading ? (
                    <div className={styles.weatherLoading}>
                      <IconCloudStorm size={24} className={styles.loadingIcon} />
                      <Typography>Loading weather data...</Typography>
                    </div>
                  ) : data.error ? (
                    <div className={styles.errorState}>
                      <IconAlertTriangle size={24} />
                      <Typography>{data.error}</Typography>
                    </div>
                  ) : data.weather ? (
                    <div className={styles.weatherContent}>
                      <div className={styles.currentWeather}>
                        <div className={styles.timeDisplay}>
                          <Typography variant="body2">
                            Current as of {dayjs().tz('America/Toronto').format('h:mm A')}
                          </Typography>
                        </div>
                        <div className={styles.mainWeather}>
                          <div className={styles.temperature}>
                            <span className={styles.tempValue}>{data.weather.current.temperature}°</span>
                            <span className={styles.tempUnit}>C</span>
                          </div>
                          <div className={styles.conditions}>
                            <Typography variant="h6">
                              {data.weather.current.conditions}
                              {(data.weather.current.precipitation?.probability > 70 ||
                                data.weather.current.wind?.speed > 50) && (
                                <IconAlertTriangle size={20} className={styles.warningIcon} />
                              )}
                            </Typography>
                            <Typography variant="body2">
                              {data.weather.current.precipitation.probability}% chance of{' '}
                              {data.weather.current.precipitation.type || 'precipitation'}
                            </Typography>
                          </div>
                        </div>
                        <div className={styles.weatherDetails}>
                          <div className={styles.detailItem}>
                            <Typography variant="body2">Wind Speed</Typography>
                            <Typography variant="body1">{data.weather.current.wind.speed} km/h</Typography>
                          </div>
                          <div className={styles.detailItem}>
                            <Typography variant="body2">Wind Direction</Typography>
                            <div className={styles.windDirection}>
                              <IconWind
                                size={20}
                                style={{
                                  transform: `rotate(${data.weather.current.wind.direction}deg)`,
                                  marginRight: '8px'
                                }}
                              />
                              <Typography variant="body1">
                                {getWindDirection(data.weather.current.wind.direction)}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </div>
    
                      {/* Hourly Forecast */}
                      {data.weather.hourly?.length > 0 && (
                        <div className={styles.hourlyForecast}>
                          <Typography variant="subtitle2" className={styles.forecastTitle}>
                            Next 4 Hours
                          </Typography>
                          <div className={styles.hourlyGrid}>
                            {data.weather.hourly.slice(0, 4).map((hour, index) => (
                              <div key={index} className={styles.hourlyItem}>
                                <Typography variant="body2" className={styles.hourTime}>
                                  {hour.time}
                                </Typography>
                                <Typography variant="h6" className={styles.hourTemp}>
                                  {hour.temperature}°
                                </Typography>
                                <Typography variant="body2" className={styles.hourlyCondition}>
                                  {hour.conditions}
                                </Typography>
                                {hour.precipitation?.probability > 30 && (
                                  <Typography variant="caption" className={styles.precipInfo}>
                                    {hour.precipitation.probability}% {hour.precipitation.type}
                                  </Typography>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <IconCloudStorm size={24} />
                      <Typography>No weather data available</Typography>
                    </div>
                  )}
                </Card>
    
                {/* Events Card */}
                <Card className={styles.modalCard}>
                  <div className={styles.cardHeader}>
                    <Typography variant="h6">Storms</Typography>
                  </div>
                  {data.events?.length > 0 ? (
                    <div className={styles.eventsGrid}>
                      {data.events.slice(0, 3).map((event, index) => (
                        <div key={index} className={styles.eventCard}>
                          <div className={styles.eventHeader}>
                            <div className={styles.eventHeaderLeft}>
                              <span className={styles.eventType}>{event.weatherPrimary}</span>
                              {event.ongoing && (
                                <Tag color="error" className={styles.eventTag}>
                                  Ongoing
                                </Tag>
                              )}
                            </div>
                            <span className={styles.eventDuration}>{event.hours}h</span>
                          </div>
                          <div className={styles.eventTimeRange}>
                            <IconCalendar size={12} className={styles.eventIcon} />
                            <span>{event.formattedStartDate} - {event.formattedEndDate}</span>
                          </div>
                          {event.totalSnowCM > 0 && (
                            <div className={styles.eventMetric}>
                              <IconSnowflake size={12} className={styles.eventIcon} />
                              <span>{event.totalSnowCM} cm snow</span>
                            </div>
                          )}
                          {event.maxPop > 0 && (
                            <div className={styles.eventMetric}>
                              <IconDroplet size={12} className={styles.eventIcon} />
                              <span>{event.maxPop}% probability</span>
                            </div>
                          )}
                        </div>
                      ))}
                      {data.events.length > 3 && (
                        <Button 
                          size="small" 
                          className={styles.showMoreButton}
                          onClick={() => {
                            Modal.info({
                              title: 'All Weather Events',
                              width: 600,
                              content: (
                                <div className={styles.allEventsModal}>
                                  {data.events.map((event, index) => (
                                    <div key={index} className={styles.eventCard}>
                                      <div className={styles.eventHeader}>
                                        <div className={styles.eventHeaderLeft}>
                                          <span className={styles.eventType}>{event.weatherPrimary}</span>
                                          {event.ongoing && (
                                            <Tag color="error" className={styles.eventTag}>
                                              Ongoing
                                            </Tag>
                                          )}
                                        </div>
                                        <span className={styles.eventDuration}>{event.hours}h</span>
                                      </div>
                                      <div className={styles.eventTimeRange}>
                                        <IconCalendar size={12} className={styles.eventIcon} />
                                        <span>{event.formattedStartDate} - {event.formattedEndDate}</span>
                                      </div>
                                      {event.totalSnowCM > 0 && (
                                        <div className={styles.eventMetric}>
                                          <IconSnowflake size={12} className={styles.eventIcon} />
                                          <span>{event.totalSnowCM} cm snow</span>
                                        </div>
                                      )}
                                      {event.maxPop > 0 && (
                                        <div className={styles.eventMetric}>
                                          <IconDroplet size={12} className={styles.eventIcon} />
                                          <span>{event.maxPop}% probability</span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ),
                              okText: 'Close'
                            });
                          }}
                        >
                          Show All Events ({data.events.length})
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <IconCloudStorm size={20} />
                      <Typography variant="body2">No weather events</Typography>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </div>
        );
      };
    return (
        <Modal
            title={
            <div className={styles.modalHeader}>
                <IconCloudStorm size={24} style={{ color: '#034832' }} />
                <Typography variant="h6">
                Configure {selectedRegionForEdit}
                </Typography>
            </div>
            }
            visible={!!selectedRegionForEdit}
            onCancel={() => setSelectedRegionForEdit(null)}
            footer={[
            <Button key="close" onClick={() => setSelectedRegionForEdit(null)}>
                Done
            </Button>
            ]}
            width={1000}
            className={`${styles.regionModal} ${styles.modal}`}
            getContainer={false}
        >
            {selectedRegionForEdit && renderRegionModal(selectedRegionForEdit)}
        </Modal>
    )
}